import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import {
  ApplicationStatusId,
  ApplicationStatusText,
  ReduxApplication,
} from "../types/Application";
import { Applicant, RiskColorEnum, RiskLevelEnum } from "src/types/Applicant";
import { Configuration } from "src/types/Redux";
import {
  getConfigurations,
  getIsFurtherReviewEnabledForApplication,
} from "src/services/AdminDashService";

dayjs.extend(utc);

const APPLICATION_STATUSES = [
  { id: 0, appStatus: "Invite Sent" },
  { id: 1, appStatus: "In Progress" },
  { id: 2, appStatus: "Rejected" },
  { id: 3, appStatus: "Completed" },
  { id: 4, appStatus: "Removed" },
  { id: 5, appStatus: "Credit and ID Returned" },
  { id: 6, appStatus: "Lease Pending" },
  { id: 7, appStatus: "Lease Signed" },
  { id: 8, appStatus: "Credit and ID Pending" },
  { id: 9, appStatus: "Cancelled" },
  { id: 10, appStatus: "Approved" },
  { id: 11, appStatus: "Closed" },
  { id: 12, appStatus: "Action Required" },
  { id: 13, appStatus: "Application Complete" },
  { id: 14, appStatus: "Lease Ready" },
  { id: 15, appStatus: "Document Expired" },
  { id: 16, appStatus: "Lease Declined" },
  { id: 17, appStatus: "Lease Generation Failed" },
];

const capitalize = (sentence) => {
  if (!sentence) return;
  const words = sentence.toLowerCase().split(" ");
  return words
    .map((word) => {
      if (!word.length) return "";
      return word[0].toUpperCase() + word.substring(1);
    })
    .join(" ");
};

const formatDate = (date) => {
  if (!date) return;
  return dayjs.utc(date).format("M/D/YYYY");
};

const formatTime = (date) => {
  return dayjs(date).format("h:mma");
};

const convertToLocalAndFormatDate = (date) => {
  if (!date) return;
  return dayjs(date).format("M/D/YYYY");
};

const formatUnit = (unitId) => {
  const unitArray = unitId.split("-");
  return unitArray.pop();
};

const filterBySearch = (apps, searchTerms) => {
  if (searchTerms === undefined || searchTerms === "" || searchTerms === null)
    return apps;
  const searchTermsCaps = searchTerms.trim().toUpperCase();

  return apps.filter((app) => {
    const completeUnitId = `${app.communityId}-${app.phaseId}-${app.buildingId}-${app.unitId}`;

    const unitIdMatch =
      completeUnitId && completeUnitId.toUpperCase().includes(searchTermsCaps);
    const primaryName =
      `${app.primaryApplicantFirstName} ${app.primaryApplicantLastName}`.toUpperCase();
    const primaryNameMatch = primaryName.includes(searchTermsCaps);
    const secondaryNameMatch =
      app.applicants &&
      app.applicants.some((applicant) => {
        const secondaryName =
          `${applicant.firstName} ${applicant.lastName}`.toUpperCase();
        return secondaryName.includes(searchTermsCaps);
      });
    return unitIdMatch || primaryNameMatch || secondaryNameMatch;
  });
};

const hasFurtherReviewRisk = (app, configuration) => {
  const isFurtherReviewEnabled = getIsFurtherReviewEnabledForApplication(
    configuration?.features,
    app
  );

  if (!isFurtherReviewEnabled) return false;

  // Risk is now derived from further-review flags on each applicant,
  // so check all applicants instead of selecting by max riskScore.
  for (const applicant of app.applicants || []) {
    const { risk } = getRiskLevel(applicant);
    if (risk !== null) {
      return true;
    }
  }

  return false;
};

const filterBySearchAndAction = (
  apps,
  searchTerms,
  actionRequiredId,
  configuration
) => {
  const translateActionId = {
    "avb-actions-total": "totalActionsRequired",
    "avb-actions-help-requests": "helpRequestCount",
    "avb-actions-inc-stale-items": "incStaleItemsCount",
    "avb-actions-ready-to-finish": "readyToFinishCount",
    "avb-actions-risk-assessments": "applicants",
  };
  const actionPropName = translateActionId[actionRequiredId];

  // if no applications available
  if (!apps) return [];

  const filteredBySearch = filterBySearch(apps, searchTerms);
  const filteredByAction = filteredBySearch.filter((app) => {
    if (actionRequiredId === "avb-actions-total") {
      return (
        app["helpRequestCount"] > 0 ||
        app["incStaleItemsCount"] ||
        app["readyToFinishCount"] > 0 ||
        hasFurtherReviewRisk(app, configuration)
      );
    } else if (actionRequiredId === "avb-actions-risk-assessments") {
      return hasFurtherReviewRisk(app, configuration);
    } else {
      return app[actionPropName] > 0;
    }
  });

  return actionRequiredId ? filteredByAction : filteredBySearch;
};

const getHelpRequestsSum = (applications: ReduxApplication[]): number => {
  const totalHelpRequestsCount = applications.reduce(
    (prev, curr) => prev + (curr?.helpRequestCount || 0),
    0
  );
  return totalHelpRequestsCount;
};

const getGenderEquivalent = (gender) => {
  switch (gender) {
    case "F":
      return "Female";
    case "M":
      return "Male";
    case "B":
      return "Non-Binary";
    case "T":
      return "Transgender";
    case "D":
      return "Prefer Not to Say";
    case "O":
      return "Other";
    default:
      return "Other";
  }
};

const getIncStaleItemsSum = (applications) => {
  return applications.reduce((prev, curr) => {
    return prev + (curr.incStaleItemsCount ? 1 : 0);
  }, 0);
};

const getReadyToFinishSum = (applications) => {
  return applications.reduce((prev, curr) => {
    return prev + (curr.readyToFinishCount ? 1 : 0);
  }, 0);
};

const getRiskAssessmentsSum = (applications, configuration) => {
  return applications.reduce((prev, curr) => {
    const isFurtherReviewEnabled = getIsFurtherReviewEnabledForApplication(
      configuration?.features,
      curr
    );

    if (!isFurtherReviewEnabled) {
      return prev;
    }

    // Determine if any applicant on this application is considered at risk
    const hasRisk = (curr.applicants || []).some((applicant) => {
      const result = getRiskLevel(applicant);
      return !!result?.risk;
    });

    if (hasRisk) {
      return prev + 1;
    }
    return prev;
  }, 0);
};

const formatSSN = (ssn) => {
  if (!ssn) return;
  const split = ssn.split("");
  return "*** ** " + split.slice(split.length - 4).join("");
};

const getStatusName = (statusId) => {
  const status = APPLICATION_STATUSES.find((status) => statusId === status.id);
  return status ? status.appStatus : "";
};

const formatPhone = (phone) => {
  if (!phone) return;

  // remove extra characters
  const regEx = /[\(\)\-\s]*/g; // matches '(', ')', space and '-'
  phone = phone.replace(regEx, "");

  const areaCode = phone.slice(0, 3);
  const firstThree = phone.slice(3, 6);
  const lastFour = phone.slice(6);
  return `(${areaCode}) ${firstThree}-${lastFour}`;
};

const getLatestDate = (app) => {
  const dates: string[] = [];

  app.applicants.forEach((applicant) => {
    if (applicant.applicationProcess) {
      if (applicant.applicationProcess.modifiedDate)
        dates.push(applicant.applicationProcess.modifiedDate);
      if (applicant.applicationProcess.createdDate)
        dates.push(applicant.applicationProcess.createdDate);
    }
  });

  if (dates.length) {
    dates.sort((a, b) => Date.parse(b) - Date.parse(a));
    return dates[0];
  }

  return null;
};

const applicationStatusLogicalOrder: { [id: number]: number } = {
  [ApplicationStatusId.InviteSent]: 9,
  [ApplicationStatusId.InProgress]: 10,
  [ApplicationStatusId.Rejected]: 11,
  [ApplicationStatusId.Completed]: 12,
  [ApplicationStatusId.Removed]: 13,
  [ApplicationStatusId.CreditAndIDReturned]: 16,
  [ApplicationStatusId.LeaseGenerationFailed]: 20,
  [ApplicationStatusId.LeasePending]: 21,
  [ApplicationStatusId.LeaseSigned]: 22,
  [ApplicationStatusId.CreditAndIDPending]: 14,
  [ApplicationStatusId.Cancelled]: 23,
  [ApplicationStatusId.Approved]: 24,
  [ApplicationStatusId.Closed]: 25,
  [ApplicationStatusId.ActionRequired]: 15,
  [ApplicationStatusId.ApplicationComplete]: 26,
  [ApplicationStatusId.LeaseReady]: 19,
  [ApplicationStatusId.LeaseExpired]: 17,
  [ApplicationStatusId.LeaseDeclined]: 18,
};
// From Legacy--->
// private static string GetApplicationStatus(ICollection<DAL.Models.ApplicationProcess> applicationProcess) =>
//   applicationProcess
//       .Where(x => !x.IsSoftDelete)
//       .OrderBy(x => x.ApplicationStatus.LogicalOrder)
//       .FirstOrDefault()
//       ?.ApplicationStatus?.AppStatus ?? "None";
//
const getApplicationProcessStatusId = (app: ReduxApplication) => {
  const applicant = app.applicants.reduce(
    (prev, cur) =>
      prev === undefined ||
      applicationStatusLogicalOrder[
        prev.applicationProcess.applicationStatusId
      ] >
        applicationStatusLogicalOrder[
          cur.applicationProcess.applicationStatusId
        ]
        ? cur
        : prev,
    <Applicant | undefined>undefined
  );
  return applicant?.applicationProcess?.applicationStatusId;
};

const addFullNameAndlastUpdated = ([...applications]: ReduxApplication[]) => {
  let apps = applications.map(({ ...x }) => {
    let lastUpdated;
    const applicationProcessStatusId = getApplicationProcessStatusId(x);
    if (
      applicationProcessStatusId === ApplicationStatusId.CreditAndIDReturned
    ) {
      lastUpdated = getLatestDate(x);
    } else {
      lastUpdated = x.modifiedDate;
    }

    if (!x.primaryApplicantFirstName) x.primaryApplicantFirstName = "";
    if (!x.primaryApplicantLastName) x.primaryApplicantLastName = "";

    // AP2-6711: Lease Domain applications carry their own richer status rollup
    // (application.applicationStatus, AP2-6681's getLeaseDomainApplicationStatus) that covers
    // states - Screening Pending, Approved with Conditions, Pending Signature, Document Voided,
    // etc. - the legacy applicationStatusId/APPLICATION_STATUSES lookup below has no id for. Use
    // it directly instead of the legacy per-applicant applicationStatusId cascade, which for a
    // Lease Domain application only ever reflects a coarser, separately-computed legacy-analog id.
    const applicationProcessStatusText =
      typeof x.applicationId === "string" &&
      isLeaseDomainApplicationId(x.applicationId)
        ? x.applicationStatus
        : APPLICATION_STATUSES.find(
            (item) => item.id === applicationProcessStatusId
          )?.appStatus;

    return {
      ...x,
      fullName: `${x.primaryApplicantFirstName} ${x.primaryApplicantLastName}`,
      lastUpdated,
      applicationProcessStatusId,
      applicationProcessStatusText,
    };
  });
  return apps;
};

/**
 * Helper function for creating chunks or arrays
 *      from based on the size.
 * For example chunk([1,2,3,4], 2)
 *             [[1,2],[3,4]]
 * @param {*} arr
 * @param {*} size
 * @returns
 */
const chunk = (arr, size) => {
  return arr.reduce((chunks, item, index) => {
    return (
      index % size ? chunks[chunks.length - 1].push(item) : chunks.push([item]),
      chunks
    );
  }, []);
};

const formatCurrency = (amount) => {
  if (amount == null) return;

  var formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  });

  return formatter.format(amount);
};

// Mirrors AppLease.AdminDashboard.API's isLeaseDomainApplicationId/UUID_REGEX
// (src/handlers/get-application-by-id.js): legacy applications use a numeric id, Lease Domain
// applications use a UUID string - the id format itself tells you which system owns the record,
// no phase-config lookup involved (AP2-7014: the routing check for every AdminDash action).
const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const isLeaseDomainApplicationId = (applicationId: string | undefined) =>
  typeof applicationId === "string" && UUID_REGEX.test(applicationId);

// AP2-7012: joins only the non-empty name parts, so a null part never renders as "null".
const formatFullName = (
  person:
    | {
        firstName?: string | null;
        middleName?: string | null;
        lastName?: string | null;
      }
    | undefined
) =>
  [person?.firstName, person?.middleName, person?.lastName]
    .filter(Boolean)
    .join(" ");

// Community T&C PDF: {documentsBaseUrl}/{phaseId}/tc.pdf, or null when either is missing.
const buildLeaseDomainTermsAndConditionsUrl = (
  documentsBaseUrl: string | undefined,
  phaseId: string | undefined
): string | null => {
  const baseUrl = documentsBaseUrl?.trim().replace(/\/+$/, "");
  const phase = phaseId?.trim();
  if (!baseUrl || !phase) return null;
  return `${baseUrl}/${encodeURIComponent(phase)}/tc.pdf`;
};

// AP2-6506: shared by ApplicationDetails.tsx for both its help-requests sources - the legacy
// Redux-populated list (from POST /help-requests/bulk-fetch) and, for a Lease Domain (UUID)
// application, application.helpRequests read straight off the already-fetched application object
// instead of a separate bulk-fetch call. Keeps the "new, most-recently-updated first" filtering/
// sorting identical regardless of source.
const filterAndSortNewHelpRequests = (
  helpRequests:
    | { helpStatus: string; createdDate?: string; modifiedDate?: string }[]
    | undefined
    | null
) =>
  (helpRequests ?? [])
    .filter((request) => request.helpStatus?.toLowerCase() === "new")
    .sort((a, b) => {
      // Review follow-up: coalesce to 0 (epoch) when both modifiedDate and createdDate are
      // absent/unparseable, rather than letting `false`/`undefined`/an Invalid Date reach the
      // subtraction below and produce NaN, which leaves sort order unspecified. Pre-existing
      // behavior carried over verbatim from loadHelpRequests(); LD rows always carry createdDate,
      // so this only changes an edge case that was already indeterminate, never a regression.
      const aDate =
        (a.modifiedDate && new Date(a.modifiedDate).getTime()) ||
        (a.createdDate && new Date(a.createdDate).getTime()) ||
        0;
      const bDate =
        (b.modifiedDate && new Date(b.modifiedDate).getTime()) ||
        (b.createdDate && new Date(b.createdDate).getTime()) ||
        0;
      return bDate - aDate;
    });

const isApplicationEditable = (applicant) => {
  return (
    applicant.emailAddress &&
    (applicant.nonUSPhone == true ||
      (applicant.phoneNumber && applicant.phoneNumberType)) &&
    applicant.status !== ApplicationStatusText.INVITE_SENT
  );
};

const getRiskLevel = (
  applicant?: Applicant
): {
  risk: RiskLevelEnum.HIGH | RiskLevelEnum.MEDIUM | null;
  percentage: string | null;
  color: RiskColorEnum.HIGHRISK | RiskColorEnum.MEDIUMRISK | null;
} => {
  if (!applicant) {
    return { risk: null, percentage: null, color: null };
  }

  const isHighRisk = applicant?.exclusionListFurtherReviewData?.isHighRisk;
  const isMediumRisk = applicant?.exclusionListFurtherReviewData?.isMediumRisk;

  if (!isHighRisk && !isMediumRisk) {
    return { risk: null, percentage: null, color: null };
  }

  let risk: RiskLevelEnum;

  if (isHighRisk) risk = RiskLevelEnum.HIGH;
  else if (isMediumRisk) risk = RiskLevelEnum.MEDIUM;
  else risk = RiskLevelEnum.LOW;

  if (risk === RiskLevelEnum.LOW) {
    return { risk: null, percentage: null, color: null };
  }

  const riskScore = applicant?.riskScore;
  let percentage: string | null = null;

  if (typeof riskScore === "number" && !Number.isNaN(riskScore)) {
    percentage = (riskScore * 100).toFixed(1);
  }

  return {
    risk,
    percentage,
    color:
      risk === RiskLevelEnum.HIGH
        ? RiskColorEnum.HIGHRISK
        : RiskColorEnum.MEDIUMRISK,
  };
};

export {
  capitalize,
  convertToLocalAndFormatDate,
  chunk,
  formatDate,
  formatTime,
  formatUnit,
  filterBySearch,
  filterBySearchAndAction,
  getGenderEquivalent,
  getHelpRequestsSum,
  getReadyToFinishSum,
  getRiskAssessmentsSum,
  getIncStaleItemsSum,
  formatSSN,
  getStatusName,
  formatPhone,
  addFullNameAndlastUpdated,
  formatCurrency,
  isApplicationEditable,
  getRiskLevel,
  isLeaseDomainApplicationId,
  formatFullName,
  buildLeaseDomainTermsAndConditionsUrl,
  filterAndSortNewHelpRequests,
};
